import { ref, onBeforeUnmount } from 'vue'
import { useAuthStore } from '@/stores/auth.js'
import { useToast } from '@/composables/useToast.js'
import { useTranslationsStore } from '@/stores/translations.js'

export function useFriends() {
    const authStore = useAuthStore()
    const { addToast: toast } = useToast()
    const { t } = useTranslationsStore()

    // Reactive data
    const friendSearchQuery = ref('')
    const searchResults = ref([])
    const isSearching = ref(false)
    const searchTimeout = ref(null)
    const loadingFriendId = ref(null)
    const cancelingRequestId = ref(null)

    // Cleanup timeout on unmount
    onBeforeUnmount(() => {
        if (searchTimeout.value) {
            clearTimeout(searchTimeout.value)
        }
    })

    // Debounced search function
    const debouncedSearch = () => {
        if (searchTimeout.value) {
            clearTimeout(searchTimeout.value)
        }

        searchTimeout.value = setTimeout(() => {
            searchFriends()
        }, 300)
    }

    // Search friends function
    const searchFriends = async () => {
        const query = friendSearchQuery.value.trim()

        if (!query) {
            searchResults.value = []
            return
        }

        isSearching.value = true
        try {
            const results = await authStore.searchProfiles(query)
            searchResults.value = results
        } catch (error) {
            console.error('Failed to search friends:', error)
            searchResults.value = []
        } finally {
            isSearching.value = false
        }
    }

    // Send friend request function
    const sendFriendRequest = async (friend, options = {}) => {
        loadingFriendId.value = friend.id
        try {
            await authStore.sendFriendRequest(friend.id)

            // Clear search if requested
            if (options.clearSearch) {
                friendSearchQuery.value = ''
                searchResults.value = []
            }

            return { success: true }
        } catch (error) {
            console.error('Failed to send friend request:', error)

            if (!options.silent) {
                const serverMessage = error.data?.message
                const isBlocked =
                    error.status === 400 && serverMessage === 'Unable to send Friend request'

                toast(
                    isBlocked ? t('friends.request_blocked_title') : t('general.something_went_wrong'),
                    5000,
                    isBlocked
                        ? t('friends.request_blocked_body')
                        : serverMessage || t('friends.error_send_request'),
                )
            }

            return { success: false, error }
        } finally {
            loadingFriendId.value = null
        }
    }

    // Cancel friend request function
    const cancelFriendRequest = async (request, options = {}) => {
        cancelingRequestId.value = request.id
        try {
            await authStore.cancelFriendRequest(request.id)
            return { success: true }
        } catch (error) {
            console.error('Failed to cancel friend request:', error)

            if (!options.silent) {
                toast(
                    t('general.something_went_wrong'),
                    5000,
                    error.data?.message || t('friends.error_cancel_request'),
                )
            }

            return { success: false, error }
        } finally {
            cancelingRequestId.value = null
        }
    }

    // Clear search results
    const clearSearch = () => {
        friendSearchQuery.value = ''
        searchResults.value = []
        isSearching.value = false
    }

    return {
        // Data
        friendSearchQuery,
        searchResults,
        isSearching,
        loadingFriendId,
        cancelingRequestId,

        // Methods
        debouncedSearch,
        searchFriends,
        sendFriendRequest,
        cancelFriendRequest,
        clearSearch,
    }
}
